import React from 'react';
import {
    AutocompleteRenderInputParams,
    FormControl,
    FormHelperText,
    TextField,
    Autocomplete,
    TextFieldVariants,
    PopperProps,
    Checkbox,
    Typography,
    styled,
    InputProps
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Field, FieldAttributes, FieldProps } from 'formik';
import { Option } from 'lib/types';
import { Column } from 'ui/Flex';
import { isDefined } from 'lib/typeguards';

interface AutocompleteFormFieldProps {
    label?: React.ReactNode;
    placeholder?: string;
    options: Option[];
    description?: React.ReactNode;
    isCreatable?: boolean;
    disabled?: boolean;
    className?: string;
    multiple?: boolean;
    native?: boolean;
    emptyLabel?: string;
    hasAllOption?: boolean;
    selectAllOptionLabel?: string;
    disableClearable?: boolean;
    getOptionDisabled?: (option: Option) => boolean;
    onSelect?: (value: string | string[]) => void;
    readOnly?: boolean;
    textFieldVariant?: TextFieldVariants;
    size?: 'small' | 'medium';
    popperProps?: Partial<PopperProps>;
    limitTags?: number;
    openByDefault?: boolean;
    inputProps?: Partial<InputProps>;
}

const checkedIcon = <CheckBoxIcon fontSize="small" />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;

const ALL_OPTION = '*';

const StyledAutocomplete = styled(Autocomplete)({
    '& .MuiAutocomplete-tag': {
        maxWidth: 150
    }
});

export const AutocompleteFormField: React.FC<FieldAttributes<AutocompleteFormFieldProps>> = ({
    label,
    description,
    options,
    multiple,
    hasAllOption,
    disabled,
    limitTags,
    popperProps,
    placeholder,
    openByDefault,
    inputProps,
    onSelect,
    getOptionDisabled,
    textFieldVariant,
    size,
    disableClearable,
    ...props
}) => (
    <Field {...props}>
        {(childProps: FieldProps) => (
            <AutocompleteFormFieldComponent
                label={label}
                description={description}
                options={options}
                multiple={multiple}
                hasAllOption={hasAllOption}
                disabled={disabled}
                limitTags={limitTags}
                popperProps={popperProps}
                placeholder={placeholder}
                openByDefault={openByDefault}
                onSelect={onSelect}
                getOptionDisabled={getOptionDisabled}
                textFieldVariant={textFieldVariant}
                size={size}
                disableClearable={disableClearable}
                inputProps={inputProps}
                {...childProps}
            />
        )}
    </Field>
);

const AutocompleteFormFieldComponent: React.FC<FieldProps & AutocompleteFormFieldProps> = ({
    label,
    description,
    meta,
    options,
    field,
    popperProps,
    emptyLabel,
    multiple,
    disableClearable,
    textFieldVariant,
    hasAllOption,
    selectAllOptionLabel,
    readOnly,
    disabled,
    placeholder,
    onSelect,
    getOptionDisabled,
    size,
    inputProps,
    openByDefault = false,
    limitTags = 2
}) => {
    const isError = meta.touched && !!meta.error;
    const [open, setOpen] = React.useState(openByDefault);

    const [inputValue, setInputValue] = React.useState('');

    const selectedOptions = React.useMemo(() => {
        if (multiple) {
            if (Array.isArray(field.value) && field.value.length > 0) {
                return options
                    .filter(option => field.value.includes(option.value))
                    .map(option => ({ ...option }));
            }
            return [];
        }
        if (field.value && typeof field.value === 'string') {
            const found = options.find(option => option.value === field.value);
            return found ? { ...found } : null;
        }
        return null;
    }, [field.value, options, multiple]);

    const computedOptions = React.useMemo(() => {
        const availableOptions = [...options];
        if (hasAllOption && multiple) {
            availableOptions.unshift({ label: selectAllOptionLabel || 'Select all', value: ALL_OPTION });
        }

        if (multiple) {
            const selectedArr = Array.isArray(selectedOptions) ? selectedOptions : [];
            if (!selectedArr.length) {
                return options.map(option => ({ ...option }));
            }
            const selectedValues = selectedArr.map(opt => opt.value);
            const selectedSet = new Set(selectedValues);
            const selected = selectedValues
                .map(val => options.find(opt => opt.value === val))
                .filter(Boolean)
                .map(option => ({ ...option }));
            const unselected = options
                .filter(opt => !selectedSet.has(opt.value))
                .map(option => ({ ...option }));
            return [...selected, ...unselected];
        }

        return availableOptions;
    }, [hasAllOption, multiple, options, selectAllOptionLabel, selectedOptions]);

    const handleChange = React.useCallback(
        (_: never, selected: Option | Option[] | null) => {
            let customEvent;
            if (
                multiple &&
                hasAllOption &&
                Array.isArray(selected) &&
                selected.some(option => option.value === ALL_OPTION)
            ) {
                customEvent = { target: { value: options.map(({ value }) => value), name: field.name } };
            } else if (multiple) {
                const selectedArr = Array.isArray(selected) ? selected : ([] as Option[]);
                const cleanSelectedOptions = selectedArr.map(option => ({
                    value: option.value,
                    label: option.label
                }));
                const newValue = cleanSelectedOptions
                    .filter(item => options.some(option => option.value === item.value))
                    .map(item => item.value);
                if (disableClearable && newValue.length === 0) {
                    return;
                }
                customEvent = { target: { value: newValue, name: field.name } };
            } else {
                const cleanSelectedOption =
                    selected && !Array.isArray(selected)
                        ? { value: selected.value, label: selected.label }
                        : null;
                customEvent = {
                    target: {
                        value: cleanSelectedOption ? cleanSelectedOption.value : null,
                        name: field.name
                    }
                };
            }
            field.onChange(customEvent);
            onSelect?.(customEvent.target.value);
        },
        [field, multiple, onSelect, hasAllOption, options, disableClearable]
    );

    const handleInputChange = React.useCallback(
        (_: never, newInputValue: string, reason: string) => {
            if (reason === 'reset' && !!multiple) {
                return;
            }
            setInputValue(newInputValue);
        },
        [multiple]
    );

    const handleClose = React.useCallback(() => {
        setOpen(false);
        setInputValue('');
    }, []);

    const handleOpen = React.useCallback(() => {
        setOpen(true);
    }, []);

    const getOptionsLabel = React.useCallback(
        (item: Option) => (typeof item.label === 'string' ? item.label : ''),
        []
    );

    const placeholderText = React.useMemo(() => {
        const text = placeholder ?? 'Search';
        if (multiple && Array.isArray(selectedOptions) && selectedOptions.length > 0 && !open) {
            return undefined;
        }
        return text;
    }, [multiple, selectedOptions, placeholder, open]);

    const renderInput = React.useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                variant={textFieldVariant}
                error={isError}
                label={label}
                size={size}
                fullWidth
                placeholder={placeholderText}
                inputProps={
                    isDefined(inputProps)
                        ? { ...(inputProps as Record<string, unknown>), ...params.inputProps }
                        : params.inputProps
                }
            />
        ),
        [isError, label, textFieldVariant, placeholderText, size, inputProps]
    );

    const renderOption = React.useCallback(
        (
            props: React.HTMLAttributes<HTMLLIElement> & { key: string },
            option: Option,
            { selected }: { selected: boolean }
        ) => {
            if (!multiple) {
                return (
                    <li {...props} key={`select-${option.value ?? option.label}`}>
                        <Column>
                            <Typography variant="body2">{option.label}</Typography>
                            {React.isValidElement(option.description) ? (
                                option.description
                            ) : (
                                <Typography variant="caption">{option.description}</Typography>
                            )}
                        </Column>
                    </li>
                );
            }
            return (
                <li {...props} key={`select-${option.value ?? option.label}`} style={{ paddingInline: 1 }}>
                    <Checkbox
                        icon={uncheckedIcon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 2 }}
                        checked={selected}
                        size="small"
                    />
                    <Column>
                        <Typography variant="body2">{option.label}</Typography>
                        {React.isValidElement(option.description) ? (
                            option.description
                        ) : (
                            <Typography variant="caption">{option.description}</Typography>
                        )}
                    </Column>
                </li>
            );
        },
        [multiple]
    );

    const renderNoTags = React.useCallback((): React.ReactNode => null, []);

    const equalityCheck = React.useCallback(
        (option: Option, value: Option) => option.value === value.value,
        []
    );

    return (
        <FormControl fullWidth error={meta.touched && !!meta.error}>
            <StyledAutocomplete
                value={selectedOptions}
                options={computedOptions}
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                onChange={handleChange}
                onInputChange={handleInputChange}
                inputValue={inputValue}
                getOptionLabel={getOptionsLabel}
                renderInput={renderInput}
                renderOption={renderOption}
                limitTags={limitTags}
                multiple={!!multiple}
                disabled={disabled}
                disableCloseOnSelect={!!multiple}
                fullWidth
                size={size}
                noOptionsText={emptyLabel}
                disableClearable={disableClearable}
                isOptionEqualToValue={equalityCheck}
                getOptionDisabled={getOptionDisabled}
                readOnly={readOnly}
                renderValue={open ? renderNoTags : undefined}
                slotProps={{
                    popper: {
                        ...popperProps
                    }
                }}
            />
            {(isError || description) && (
                <FormHelperText sx={{ marginX: 0 }} error={isError}>
                    {isError ? meta.error : description}
                </FormHelperText>
            )}
        </FormControl>
    );
};
